"use client";

import type { CSSProperties } from "react";

/**
 * FacetPane — one iTunes-style column: label header, optional "הכל (N)" row,
 * value rows with live counts. Pure presentational: props in, callbacks out,
 * no data fetching. `depth` indents a value row — tree VISUAL only, tree
 * selection semantics (select-includes-descendants) land in S4.
 *
 * Styled entirely via Brand v2 CSS vars (--brand-teal, --border-hairline,
 * --fg-*) — no raw hex, no Tailwind color literals, so it stays data- and
 * skin-agnostic across products.
 */
export interface FacetValueRow {
  value: string;
  /** Display label is separate from the stable selection value. */
  label?: string;
  count: number;
  depth?: number;
}

export interface FacetPaneProps {
  label: string;
  values: FacetValueRow[];
  selected: string[];
  onToggle: (value: string) => void;
  onClear?: () => void;
  showAll?: boolean;
  maxHeight?: number;
  /** Exact matching row count, including rows without a value in this pane. */
  totalCount?: number;
}

export function FacetPane({
  label,
  values,
  selected,
  onToggle,
  onClear,
  showAll = true,
  maxHeight = 224,
  totalCount,
}: FacetPaneProps) {
  // Every row is single-valued in the common case, so summing this facet's
  // own (ignore-self) counts reproduces the total row count matching every
  // OTHER active filter — exactly what the "הכל" row needs to show.
  const allCount = totalCount ?? values.reduce((sum, v) => sum + v.count, 0);
  const noneSelected = selected.length === 0;

  return (
    <div
      dir="rtl"
      className="flex flex-col min-w-0"
      style={{
        background: "var(--brand-slate)",
        border: "1px solid var(--border-hairline)",
        borderRadius: "var(--gam-radius-card, 0.75rem)",
        overflow: "hidden",
      }}
    >
      <div
        className="flex items-center justify-between px-3 py-1.5 shrink-0"
        style={{ borderBottom: "1px solid var(--border-hairline)", background: "var(--bg-chrome)" }}
      >
        <span className="text-xs font-medium truncate" style={{ color: "var(--fg-default)" }}>
          {label}
        </span>
        <span className="text-[10px] shrink-0" style={{ color: "var(--fg-faint)" }}>
          {values.length}
        </span>
      </div>

      <div className="overflow-y-auto" style={{ maxHeight }}>
        {showAll && (
          <FacetRow active={noneSelected} label={`הכל (${allCount})`} onClick={() => onClear?.()} />
        )}
        {values.map((v) => (
          <FacetRow
            key={v.value}
            active={selected.includes(v.value)}
            label={v.label ?? v.value}
            count={v.count}
            depth={v.depth}
            onClick={() => onToggle(v.value)}
          />
        ))}
        {values.length === 0 && (
          <div className="px-3 py-4 text-center text-[11px]" style={{ color: "var(--fg-faint)" }}>
            אין ערכים
          </div>
        )}
      </div>
    </div>
  );
}

function FacetRow({
  active,
  label,
  count,
  depth,
  onClick,
}: {
  active: boolean;
  label: string;
  count?: number;
  depth?: number;
  onClick: () => void;
}) {
  const style: CSSProperties = {
    background: active ? "var(--brand-teal-dim)" : "transparent",
    color: active ? "var(--brand-teal)" : "var(--fg-muted)",
    borderBottom: "1px solid var(--border-hairline)",
    paddingInlineStart: depth ? `${12 + depth * 12}px` : undefined,
    transitionTimingFunction: "var(--gam-spring-default, cubic-bezier(0.4, 0, 0.2, 1))",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-right transition-colors hover:opacity-80"
      style={style}
    >
      <span className="truncate">{label}</span>
      {count !== undefined && (
        <span
          className="tabular-nums shrink-0 text-[11px]"
          style={{ color: active ? "var(--brand-teal)" : "var(--fg-faint)" }}
        >
          {count}
        </span>
      )}
    </button>
  );
}
